import { Children, isValidElement, useEffect, useMemo } from 'react'
import { DatagridConfigurable, useResourceContext, useStore, useTranslate } from 'react-admin'

const EMPTY_COLUMNS = []
const EMPTY_OMIT = []

const getColumnKey = (column) => column.source ?? column.label ?? column.index

const getColumnSignature = (columns) => columns.map(getColumnKey).join('|')

const buildColumns = (children, translate) =>
  Children.toArray(children)
    .filter(isValidElement)
    .map((child, index) => ({
      index: String(index),
      source: child.props.source,
      label:
        child.props.label && typeof child.props.label === 'string'
          ? child.props.label
          : child.props.source
            ? undefined
            : translate('ra.configurable.Datagrid.unlabeled', {
                column: index,
                _: `Unlabeled column #%{column}`,
              }),
    }))

const AdvancedDatagridConfigurable = ({ children, omit, preferenceKey, ...props }) => {
  const resource = useResourceContext(props)
  const translate = useTranslate()
  const finalPreferenceKey = preferenceKey || `${resource}.datagrid`

  const omitKey = omit?.join('|')
  const stableOmit = useMemo(() => {
    if (omitKey === undefined) return undefined
    return omitKey === '' ? EMPTY_OMIT : omitKey.split('|')
  }, [omitKey])

  const [availableColumns, setAvailableColumns] = useStore(
    `preferences.${finalPreferenceKey}.availableColumns`,
    EMPTY_COLUMNS,
  )
  const [, setOmit] = useStore(`preferences.${finalPreferenceKey}.omit`, stableOmit)
  const [selectedColumns, setSelectedColumns] = useStore(`preferences.${finalPreferenceKey}.columns`)

  const currentColumns = useMemo(() => buildColumns(children, translate), [children, translate])
  const currentSignature = getColumnSignature(currentColumns)
  const storedSignature = getColumnSignature(availableColumns)

  useEffect(() => {
    if (currentSignature === storedSignature) return

    if (selectedColumns !== undefined) {
      const selectedKeys = new Set(
        selectedColumns
          .map((index) => availableColumns.find((column) => column.index === index))
          .filter(Boolean)
          .map(getColumnKey),
      )
      setSelectedColumns(
        currentColumns.filter((column) => selectedKeys.has(getColumnKey(column))).map((column) => column.index),
      )
    }

    setAvailableColumns(currentColumns)
    setOmit(stableOmit)
  }, [
    currentSignature,
    storedSignature,
    currentColumns,
    availableColumns,
    selectedColumns,
    stableOmit,
    setSelectedColumns,
    setAvailableColumns,
    setOmit,
  ])

  return (
    <DatagridConfigurable omit={stableOmit} preferenceKey={finalPreferenceKey} {...props}>
      {children}
    </DatagridConfigurable>
  )
}

export default AdvancedDatagridConfigurable
